import React, { useState, useCallback, useRef, useEffect, useMemo, memo } from "react";

// Native debounce implementation (replaces lodash/debounce)
function createDebouncedFn(fn, delay) {
  let timeoutId = null;
  const debouncedFn = (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  debouncedFn.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
  return debouncedFn;
}

const INPUT_DELAY = 250;
const MIN_QUERY_LENGTH = 3;

function ProductClassificationSearch({ railsFormName, handleClassificationChange, value }) {
  const [searchKeyword, setSearchKeyword] = useState(value ?? "");
  const [classifications, setClassifications] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState(null);
  const cache = useRef({});
  const abortControllerRef = useRef(null);
  const listboxId = `classification-listbox-${useRef(Math.random().toString(36).slice(2, 9)).current}`;

  const fetchClassifications = useMemo(
    () =>
      createDebouncedFn((query) => {
        if (!query || query.length < MIN_QUERY_LENGTH) {
          setIsFetching(false);
          return;
        }

        if (cache.current[query]) {
          setClassifications(cache.current[query]);
          setIsFetching(false);
          return;
        }

        // Cancel any in-flight request
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        setError(null);

        fetch(`/api/int/search?q=${encodeURIComponent(query)}`, {
          method: "GET",
          signal: abortControllerRef.current.signal,
        })
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
            return r.json();
          })
          .then((r) => {
            if (r.error) {
              setError(r.error);
              setClassifications([]);
            } else {
              const results = r.data || [];
              cache.current[query] = results;
              setClassifications(results);
            }
          })
          .catch((err) => {
            if (err.name !== "AbortError") {
              console.error("Search error:", err);
              setError("Search is temporarily unavailable. Please try again or use the dropdown below.");
              setClassifications([]);
            }
          })
          .finally(() => {
            setIsFetching(false);
          });
      }, INPUT_DELAY),
    []
  );

  useEffect(() => {
    return () => {
      fetchClassifications.cancel();
      abortControllerRef.current?.abort();
    };
  }, [fetchClassifications]);

  const handleChange = (event) => {
    const query = event.target.value;
    setSearchKeyword(query);
    setActiveIndex(-1);

    if (query.length >= MIN_QUERY_LENGTH) {
      setIsFetching(true);
      setShowDropdown(true);
    } else {
      setClassifications([]);
      setShowDropdown(false);
    }

    fetchClassifications(query);
  };

  const handleFocus = () => {
    if (classifications.length > 0 || searchKeyword.length >= MIN_QUERY_LENGTH) {
      setShowDropdown(true);
    }
  };

  const handleBlur = () => {
    setShowDropdown(false);
    setActiveIndex(-1);
  };

  const handleSelect = useCallback(
    (classification) => {
      handleClassificationChange(classification);
      setShowDropdown(false);
      setActiveIndex(-1);
    },
    [handleClassificationChange]
  );

  const handleKeyDown = (event) => {
    if (!showDropdown || classifications.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((prev) => (prev < classifications.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : classifications.length - 1));
        break;
      case "Enter":
        event.preventDefault();
        if (activeIndex >= 0) {
          handleSelect(classifications[activeIndex]);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setActiveIndex(-1);
        break;
    }
  };

  const showLoading = showDropdown && isFetching;
  const showError = showDropdown && !isFetching && error;
  const showResults = showDropdown && !isFetching && !error && classifications.length > 0;
  const showNoResults =
    showDropdown && !isFetching && !error && classifications.length === 0 && searchKeyword.length >= MIN_QUERY_LENGTH;

  return (
    <div className="form-group autosuggest">
      <label htmlFor="product-classification-search" className="sr-only">
        Search for a product
      </label>
      <input
        id="product-classification-search"
        type="text"
        placeholder="Search for a product..."
        name={`${railsFormName}[description]`}
        className={`form-control form-control-lg${showResults ? " autosuggest-input" : ""}`}
        value={searchKeyword}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={activeIndex >= 0 ? `classification-${classifications[activeIndex]?.id}` : undefined}
      />

      {showLoading && (
        <div id={listboxId} className="list-group autosuggest-list" role="listbox">
          <div className="list-group-item text-muted" aria-live="polite">Searching...</div>
        </div>
      )}

      {showError && (
        <div id={listboxId} className="list-group autosuggest-list" role="listbox">
          <div className="list-group-item text-danger" aria-live="assertive">
            <i className="fas fa-exclamation-circle mr-2" aria-hidden="true" />
            {error}
          </div>
        </div>
      )}

      {showNoResults && (
        <div id={listboxId} className="list-group autosuggest-list" role="listbox">
          <div className="list-group-item text-muted" aria-live="polite">
            No classifications found. Try using the dropdown below to classify manually.
          </div>
        </div>
      )}

      {showResults && (
        <div id={listboxId} className="list-group autosuggest-list" role="listbox" aria-label="Product classifications">
          {classifications.map((classification, index) => (
            <button
              type="button"
              id={`classification-${classification.id}`}
              key={classification.id}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(classification);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={`list-group-item list-group-item-action${index === activeIndex ? " active" : ""}`}
              role="option"
              aria-selected={index === activeIndex}
            >
              <span className={index === activeIndex ? "font-weight-bold" : "text-dark font-weight-bold"}>
                {classification.name}
              </span>
              <small className={`d-block ${index === activeIndex ? "" : "text-muted"}`}>
                in {classification.path}
              </small>
            </button>
          ))}
        </div>
      )}

      <hr className="divider divider-fade" />
    </div>
  );
}

export default memo(ProductClassificationSearch);
